import { Button } from 'components/atoms';
import ApplicationAnnouncements from 'components/molecules/application-announcements/ApplicationAnnouncements';
import { Navbar } from 'components/organisms';
import NavbarBurger from 'components/organisms/navbar-burger/NavbarBurger';
import { useSyncNavMemoryWithRouter } from 'hooks/useSyncNavMemoryWithRouter';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiHeadset } from 'react-icons/pi';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import './ApplicationTemplate.scss';

interface ScrollContextType {
  isAtBottom: boolean;
  isAtTop: boolean;
  scrollTop: number;
  scrollHeight: number;
  clientHeight: number;
  scrollToBottom: () => void;
}

const ScrollContext = createContext<ScrollContextType>({
  isAtBottom: false,
  isAtTop: false,
  scrollTop: 0,
  scrollHeight: 0,
  clientHeight: 0,
  scrollToBottom: () => {},
});

interface IUseScroll {
  fetchNextPage?: () => void;
  fetchNextPageOnTop?: () => void;
}

export const useScroll = ({ fetchNextPage, fetchNextPageOnTop }: IUseScroll) => {
  const scrollContext = useContext(ScrollContext);
  if (!scrollContext) {
    throw new Error('useScroll must be used within an ApplicationTemplate');
  }
  useEffect(() => {
    if (scrollContext.isAtBottom && fetchNextPage) {
      fetchNextPage();
    }
  }, [scrollContext.isAtBottom]);

  useEffect(() => {
    if (scrollContext.isAtTop && fetchNextPageOnTop) {
      fetchNextPageOnTop();
    }
  }, [scrollContext.isAtTop]);

  return scrollContext;
};

const ApplicationTemplate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [scrollState, setScrollState] = useState<Omit<ScrollContextType, 'scrollToBottom'>>({
    isAtBottom: false,
    isAtTop: false,
    scrollTop: 0,
    scrollHeight: 0,
    clientHeight: 0,
  });

  const handleScroll = useCallback(() => {
    if (!ref.current) return;

    const { scrollTop, scrollHeight, clientHeight } = ref.current;
    const isAtBottom = Math.abs(scrollTop) + clientHeight >= scrollHeight - 10; // Margines błędu 10px
    const isAtTop = Math.abs(scrollTop) <= 10;

    setScrollState({
      isAtBottom,
      isAtTop,
      scrollTop,
      scrollHeight,
      clientHeight,
    });
  }, []);

  useSyncNavMemoryWithRouter();

  useEffect(() => {
    const container = ref.current;
    if (!container) return;

    handleScroll();

    container.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);
    return () => {
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [handleScroll]);

  const scrollToBottom = useCallback(() => {
    if (!ref.current) return;
    ref.current.scrollTo({
      top: ref.current.scrollHeight,
      behavior: 'auto', // możesz zmienić na 'auto' dla natychmiastowego przewinięcia
    });
  }, []);

  return (
    <ScrollContext.Provider value={{ ...scrollState, scrollToBottom }}>
      <div className="application-template">
        <ApplicationAnnouncements />
        <div className="navbar">
          <Navbar />
          <NavbarBurger />
        </div>
        <div ref={ref} className="application">
          <Outlet />
        </div>
        {!location.pathname.includes('support') && (
          <div className="application-support-redirect">
            <Button
              variant={BUTTON_VARIANT.submit}
              onlyIcon
              size="large"
              onClick={() => navigate('/support/create')}
            >
              <Button.Description description={t('navbar.support')} position="left" />
              <Button.Icon icon={<PiHeadset />} />
            </Button>
          </div>
        )}
      </div>
    </ScrollContext.Provider>
  );
};

export default ApplicationTemplate;
